import VisuallyHidden from "@galaxy-io/dls/a11y/VisuallyHidden";
import Avatar from "@galaxy-io/dls/avatar/Avatar";
import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Chip, { ChipSize, ChipVariant } from "@galaxy-io/dls/chips/Chip";
import Divider from "@galaxy-io/dls/layout/Divider";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import type { ActivityItem } from "@gtm/contracts";
import type { ActivityPage } from "@gtm/contracts/core/activity-api";
import {
  type ReviewState,
  reviewActions,
  reviewLabels,
  reviewStates,
} from "@gtm/contracts/core/review";
import { styled } from "@linaria/react";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { RelativeTime } from "@/components/workspace/relative-time";
import { ReviewStateIcon, reviewIcons } from "@/components/workspace/review-status";
import { SourceIcon } from "@/components/workspace/source-icon";
import { apiClient, unwrap } from "@/lib/api-client";
import { ActivitySignalChip } from "./activity-signal-chip";
import { useReviews } from "./review-actions";
import { SignalLoading, SignalPlaceholder } from "./signal-placeholder";

// Lanes meet the toolbar and each other directly; hairlines carry the structure.
// Four lanes fill the board; any more scroll sideways rather than narrowing.
const Board = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  padding-bottom: ${t.space[24]};
`;
const Lane = styled.section`
  flex: 1 0 max(260px, 25%);
  min-width: 260px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 0.5px solid ${t.color.border.primary};
  transition: background-color ${t.duration.fast};
  @media (prefers-reduced-motion: reduce) { transition: none; }
  &:last-of-type {
    border-right: 0;
  }
  &[data-over="true"] {
    background: ${t.color.background.secondary};
  }
`;
const LaneHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
  min-height: 44px;
  padding: ${t.space[8]} ${t.space[12]};
  border-bottom: 0.5px solid ${t.color.border.primary};
`;
const LaneTitle = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: ${t.space[8]};
  min-width: 0;
`;
const Cards = styled.div`
  overflow-y: auto;
  min-height: 0;
  padding: ${t.space[12]};
  display: flex;
  flex-direction: column;
  gap: ${t.space[8]};
  flex: 1;
`;
const Card = styled.article`
  min-width: 0;
  cursor: grab;
  &[data-dragging="true"] { opacity: 0.4; }
`;
const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
  width: 100%;
  min-width: 0;

`;
const CardCorner = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  flex-shrink: 0;
`;
const CardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  min-width: 0;
`;
const CardDivider = styled.div`
  align-self: stretch;
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${t.space[4]};
  padding-block: ${t.space[2]};
`;
const PersonControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
`;
// The person's own status, beside their name: a card is about a signal, but
// whether the person was already emailed is what decides what to do with it.
const PersonStatus = styled.span`
  display: inline-flex;
  flex: none;
`;
const dragType = "application/x-galaxy-signal";
const emptyLanes = {
  unprocessed: {
    icon: reviewIcons.unprocessed,
    header: "No signals to review",
    message: "New signals will appear here.",
  },
  acknowledged: {
    icon: reviewIcons.acknowledged,
    header: "No acknowledged signals",
    message: "Drag signals here to keep an eye on them.",
  },
  drafted: {
    icon: reviewIcons.drafted,
    header: "No drafted signals",
    message: "Drag signals here once an email is drafted.",
  },
  saved: {
    icon: reviewIcons.saved,
    header: "No saved signals",
    message: "Drag signals here to keep them for later.",
  },
  processed: {
    icon: reviewIcons.processed,
    header: "No processed signals",
    message: "Drag signals here once they’re handled.",
  },
  ignored: {
    icon: reviewIcons.ignored,
    header: "No ignored signals",
    message: "Drag signals here when no action is needed.",
  },
};
function EmptyLane({ state, filtered }: { state: ReviewState; filtered: boolean }) {
  const empty = emptyLanes[state];
  return (
    <SignalPlaceholder
      icon={filtered ? MagnifyingGlassIcon : empty.icon}
      heading={filtered ? "No matching signals" : empty.header}
      message={filtered ? "Try changing your search or filters." : empty.message}
    />
  );
}
type BoardLane = InfiniteData<ActivityPage, number>;
type Move = { id: string; from: ReviewState; to: ReviewState };
type Drag = { id: string; from: ReviewState };
// Lanes share every filter except the review state, which the lane supplies.
function laneFilters(query: Record<string, string | undefined>) {
  return Object.fromEntries(
    Object.entries(query).filter(
      ([key, value]) =>
        value !== undefined && !["view", "page", "review", "sort", "direction"].includes(key),
    ),
  ) as Record<string, string>;
}
function laneKey(state: ReviewState, filters: Record<string, string>) {
  return ["signal-board", "occurred", state, filters];
}
// Lanes are sorted by `when` descending, then by id descending.
function when(item: ActivityItem) {
  return new Date(item.occurred_at ?? item.observed_at).getTime();
}
function findItem(lane: BoardLane | undefined, id: string) {
  return lane?.pages.flatMap((page) => page.items).find((it) => it.id === id);
}
function withoutItem(lane: BoardLane, id: string): BoardLane {
  return {
    ...lane,
    pages: lane.pages.map((page) => ({
      ...page,
      total: Math.max(0, page.total - 1),
      items: page.items.filter((item) => item.id !== id),
    })),
  };
}
function withItem(lane: BoardLane, item: ActivityItem): BoardLane {
  const at = when(item);
  const pages = lane.pages.map((page) => ({
    ...page,
    total: page.total + 1,
    items: [...page.items],
  }));
  for (const [index, page] of pages.entries()) {
    const before = page.items.findIndex(
      (existing) => when(existing) < at || (when(existing) === at && existing.id < item.id),
    );
    if (before !== -1) {
      page.items.splice(before, 0, item);
      return { ...lane, pages };
    }
    // Only the final loaded page may append; otherwise it belongs further down.
    if (index === pages.length - 1 && !page.hasNext) page.items.push(item);
  }
  return { ...lane, pages };
}
export function SignalBoard({ query }: { query: Record<string, string | undefined> }) {
  const client = useQueryClient();
  const [dragged, setDragged] = useState<Drag | null>(null);
  const [over, setOver] = useState<ReviewState | null>(null);
  const [landed, setLanded] = useState<string | null>(null);
  const [settling, setSettling] = useState(0);
  const [announcement, setAnnouncement] = useState("");
  const reviews = useReviews([]);
  const filters = laneFilters(query);
  const visibleStates = reviewStates.filter(
    (state) => !query.review || query.review.split(",").includes(state),
  );
  // Our own writes already moved the card, so skip the board-wide refetch they
  // broadcast; edits from the drawer or table still refresh every lane.
  const ourWrites = useRef(0);
  const touched = useRef(new Set<ReviewState>());
  const landing = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(landing.current), []);
  useEffect(() => {
    const refresh = () => {
      if (ourWrites.current) return;
      void client.invalidateQueries({ queryKey: ["signal-board"] });
    };
    window.addEventListener("records:changed", refresh);
    return () => window.removeEventListener("records:changed", refresh);
  }, [client]);
  const move = useMutation({
    mutationFn: async ({ id, to }: Move) => {
      if (!(await reviews.act([id], reviewActions[to])))
        throw new Error(`Could not move signal to ${reviewLabels[to]}.`);
    },
    // Move the card between lane caches now; the write settles behind it.
    onMutate: async ({ id, from, to }: Move) => {
      ourWrites.current += 1;
      setSettling((n) => n + 1);
      touched.current.add(from).add(to);
      await Promise.all([
        client.cancelQueries({ queryKey: laneKey(from, filters) }),
        client.cancelQueries({ queryKey: laneKey(to, filters) }),
      ]);
      const source = client.getQueryData<BoardLane>(laneKey(from, filters));
      const target = client.getQueryData<BoardLane>(laneKey(to, filters));
      const item = findItem(source, id);
      if (!item || !source) return undefined;
      client.setQueryData(laneKey(from, filters), withoutItem(source, id));
      if (target) client.setQueryData(laneKey(to, filters), withItem(target, item));
      setAnnouncement(`Signal moved to ${reviewLabels[to]}.`);
      setLanded(id);
      clearTimeout(landing.current);
      landing.current = setTimeout(() => setLanded(null), 900);
      return { source, target };
    },
    onError: (_error, { from, to }, previous) => {
      if (!previous) return;
      client.setQueryData(laneKey(from, filters), previous.source);
      client.setQueryData(laneKey(to, filters), previous.target);
      setLanded(null);
      setAnnouncement(`Could not move signal. It stayed in ${reviewLabels[from]}.`);
    },
    // Reconcile totals and pagination once every write is quiet; nothing waits
    // on it. Refetching sooner could serve rows a sibling move has not committed.
    onSettled: () => {
      ourWrites.current -= 1;
      setSettling((n) => n - 1);
      if (ourWrites.current) return;
      for (const lane of touched.current)
        void client.invalidateQueries({ queryKey: laneKey(lane, filters) });
      touched.current.clear();
    },
  });
  return (
    <>
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
      <Board aria-label="Signal board">
        {visibleStates.map((state) => (
          <Lane
            key={state}
            aria-label={reviewLabels[state]}
            data-over={over === state}
            onDragOver={(event) => {
              if (dragged && dragged.from !== state) {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setOver(state);
              }
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(null);
            }}
            onDrop={async (event) => {
              event.preventDefault();
              const id = event.dataTransfer.getData(dragType);
              const from = dragged?.from;
              setDragged(null);
              setOver(null);
              if (id && from && id === dragged?.id && from !== state)
                move.mutate({ id, from, to: state });
            }}
          >
            <SignalLane
              filters={filters}
              state={state}
              dragging={dragged?.id ?? null}
              landed={landed}
              settling={settling > 0}
              onDrag={(id) => setDragged({ id, from: state })}
              onDragEnd={() => {
                setDragged(null);
                setOver(null);
              }}
            />
          </Lane>
        ))}
      </Board>
    </>
  );
}
function SignalLane({
  filters,
  state,
  dragging,
  landed,
  settling,
  onDrag,
  onDragEnd,
}: {
  filters: Record<string, string>;
  state: ReviewState;
  dragging: string | null;
  landed: string | null;
  settling: boolean;
  onDrag: (key: string) => void;
  onDragEnd: () => void;
}) {
  const result = useInfiniteQuery({
    queryKey: laneKey(state, filters),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        apiClient().GET("/activity", {
          params: {
            query: {
              ...filters,
              review: state,
              sort: "when",
              direction: "desc",
              page: String(pageParam),
            },
          },
          signal,
        }),
      ),
    getNextPageParam: (page) => (page.hasNext ? page.page + 1 : undefined),
    refetchInterval: settling ? false : 30_000,
    refetchOnWindowFocus: !settling,
  });
  const { update } = useUrlState();
  const draggedCard = useRef(false);
  const items = result.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <>
      <LaneHeader>
        <LaneTitle>
          <ReviewStateIcon state={state} />
          <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
            {reviewLabels[state]}
          </Text>
        </LaneTitle>
        <Chip
          hasBorder
          isPill
          count={result.data?.pages[0].total ?? 0}
          size={ChipSize.SMALL}
          variant={ChipVariant.SECONDARY}
        />
      </LaneHeader>
      <Cards>
        {result.isPending && <SignalLoading />}
        {result.isError && (
          <QueryState error={result.error} onRetry={() => void result.refetch()} />
        )}
        {!result.isPending && !result.isError && !items.length && (
          <EmptyLane state={state} filtered={Object.values(filters).some(Boolean)} />
        )}
        {items.map((item: ActivityItem) => {
          const timestamp = item.occurred_at ?? item.observed_at;
          return (
            <Card
              key={item.id}
              draggable
              data-dragging={dragging === item.id}
              data-landed={landed === item.id}
              aria-label={`${item.name || item.login || "Unknown account"}: ${item.title}`}
              onPointerDownCapture={() => {
                draggedCard.current = false;
              }}
              onDragStart={(event) => {
                if ((event.target as Element).closest("a,[data-card-control]")) {
                  event.preventDefault();
                  return;
                }
                draggedCard.current = true;
                event.dataTransfer.setData(dragType, item.id);
                event.dataTransfer.effectAllowed = "move";
                onDrag(item.id);
              }}
              onDragEnd={onDragEnd}
            >
              <Widget
                isInteractive
                isDisabled={!item.person_id}
                ariaLabel={`Open person for signal: ${item.title || "Untitled signal"}`}
                size={WidgetSize.SMALL}
                isSelected={landed === item.id}
                onClick={(event) => {
                  if (draggedCard.current && event.detail !== 0) return;
                  if (!item.person_id) return;
                  // The card is about a person; the signal is why they surfaced.
                  update({
                    activityId: null,
                    personId: item.person_id,
                    person: null,
                    personTab: null,
                    companyId: null,
                    companyTab: null,
                    companyPage: null,
                    runId: null,
                  });
                }}
              >
                <CardRow>
                  <CardLabel>
                    <SourceIcon source={item.provider} size={14} />
                    <ActivitySignalChip activity={item} size={ChipSize.SMALL} />
                  </CardLabel>
                  <CardCorner>
                    <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                      <RelativeTime
                        value={timestamp}
                        label={item.occurred_at ? "Occurred" : "Observed"}
                      />
                    </Text>
                  </CardCorner>
                </CardRow>
                <CardDivider>
                  <Divider />
                </CardDivider>
                <CardContent>
                  {item.person_id ? (
                    <PersonControl>
                      {item.person_review && (
                        <PersonStatus
                          role="img"
                          aria-label={`Person status: ${reviewLabels[item.person_review]}`}
                          title={`Person status: ${reviewLabels[item.person_review]}`}
                        >
                          <ReviewStateIcon state={item.person_review} size={12} />
                        </PersonStatus>
                      )}
                      <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                        {item.name || item.login}
                      </Text>
                    </PersonControl>
                  ) : (
                    <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                      {item.name || item.login || "Unknown account"}
                    </Text>
                  )}
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    {item.title}
                  </Text>
                </CardContent>
                <CardDivider>
                  <Divider />
                </CardDivider>
                <CardRow>
                  <CardLabel aria-label="Assigned to">
                    {item.assignedTo && <Avatar seed={item.assignedTo.name} size={16} />}
                    <Text
                      size={TextSize.BODY_SM}
                      variant={item.assignedTo ? TextVariant.SECONDARY : TextVariant.TERTIARY}
                    >
                      {item.assignedTo?.status === "removed"
                        ? "Former member"
                        : item.assignedTo?.name || "Unassigned"}
                    </Text>
                  </CardLabel>
                  {item.provider === "github" && item.context && (
                    <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                      {item.context}
                    </Text>
                  )}
                </CardRow>
              </Widget>
            </Card>
          );
        })}
        {result.hasNextPage && (
          <Button
            label="Load more"
            variant={ButtonVariant.SECONDARY}
            isLoading={result.isFetchingNextPage}
            onClick={() => void result.fetchNextPage()}
          />
        )}
      </Cards>
    </>
  );
}
